/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import {localPersistence} from '@ion/core/src/capabilities/storage';
import {error} from '@ion/core/src/logger/logger';
import {Account, User} from '@ion/core/src/types/entities';
import {decode} from '@ion/core/src/utils/Cipher';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {
  APPLICATION_INITIALIZATION_COMPLETE
} from '@/store/actions/initialization';
import {
  EULA_DIALOG_SHOW,
  LOGIN_FAILED,
  LOGIN_REQUEST,
  LOGIN_SUCCEEDED,
  LOGOUT_FAILED,
  LOGOUT_REQUEST,
  PERSIST_CREDENTIALS,
  SET_CURRENT_ACCOUNT,
  UPDATE_CURRENT_USER
} from '@/store/actions/login';
import {ApplicationKeys} from '@/types/application';

/**
 * Provides the default credentials persisted to localStorage via the remember
 * me feature.
 *
 * @property emptyCredentials
 */
export const emptyCredentials = {
  defaultAccountId : null,
  rememberMe       : false,
  credentials      : {
    username : '',
    password : ''
  }
};

/**
 * @private
 */
const getPersisted = () => {
  const item = safeJSONParse(
    JSON.stringify(localPersistence(ApplicationKeys.BMWPortal, 'login', null) || emptyCredentials)
  );
  const {password} = item?.credentials;
  if (password) {
    try {
      item.credentials.password = decode(password);
    } catch (err) {
      error(err.toString());
    }
  }
  return item;
};

/**
 * Provides a utility which retrieves the users persisted credentials from
 * localStorage (if available), Otherwise, returns the default emptyCredentials.
 *
 * @method persisted
 */
const persisted = getPersisted();

// defines the shape of this reducers state ...
export interface LoginState {
  isAuthenticating: boolean;
  authenticated: boolean;
  isServiceError: boolean;
  isInvalid: boolean;
  persistCredentials: boolean;
  user: User;
  currentAccount: Account;
  eulaDialog: {
    show: boolean;
  }
}

// defines the initial state for the reducer ...
const initialState: LoginState = {
  isAuthenticating   : false,
  authenticated      : false,
  isServiceError     : false,
  isInvalid          : false,
  persistCredentials : persisted.rememberMe,
  user               : persisted.credentials,
  currentAccount     : null,
  eulaDialog         : {
    show : false
  }
};

// defines the reducers ...
const reducers = {
  [LOGIN_REQUEST] : (draft: LoginState) => {
    draft.isAuthenticating = true;
    draft.isServiceError = draft.isInvalid = draft.authenticated = false;
  },
  [LOGIN_SUCCEEDED] : (draft: LoginState, {user}) => {
    draft.isAuthenticating = draft.isServiceError = draft.isInvalid = false;
    draft.user = user;
  },
  [LOGIN_FAILED] : (draft: LoginState, {isInvalid}) => {
    draft.isInvalid = isInvalid;
    draft.isServiceError = !isInvalid;
    draft.authenticated = draft.isAuthenticating = false;
  },
  [PERSIST_CREDENTIALS] : (draft: LoginState, {persist}) => {
    draft.persistCredentials = persist;
  },
  [LOGOUT_REQUEST] : (draft: LoginState) => {
    draft.isAuthenticating = true;
  },
  [LOGOUT_FAILED] : (draft: LoginState) => {
    draft.isAuthenticating = false;
  },
  [APPLICATION_INITIALIZATION_COMPLETE] : (draft: LoginState) => {
    draft.authenticated = true;
  },
  [UPDATE_CURRENT_USER] : (draft: LoginState, {user}) => {
    draft.user = user;
  },
  [SET_CURRENT_ACCOUNT] : (draft: LoginState, {currentAccount}) => {
    draft.currentAccount = currentAccount;
  },
  [EULA_DIALOG_SHOW] : (draft: LoginState, {show}) => {
    draft.eulaDialog.show = show;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: LoginState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
